/**
 * PLM Daily QC — screen helpers (prototype PLM-Analysis-v13 `renderDaily` /
 * `slideOptions` / `dqResultPill`, 2026-09-28).
 *
 * The RULES live on the server (PlmDailyQcRules — grading, the day's state, the
 * slides an analyst may pick); these only turn the server's answers into what
 * the screen says, in the prototype's own words.
 */
import type {
  PlmDailyQcChoice, PlmDailyQcRow, PlmDailyQcSlide, PlmDailyQcState, PlmDailyQcStatus, PlmObservationInput,
} from './plm.api';
import type { QcRecord } from './plmWorkflow';
import type { LookupData } from '../../../components/LookupCombo';
import { fmtLocalDate, nowClientWire } from '../../../utils/datetime';

/** A slide read within this many days is flagged (prototype SOP_RECENT_DAYS). */
export const SOP_RECENT_DAYS = 7;

/** An empty read, analysed by the analyst on the record's day. */
export function blankObservation(analyzedByOid: string | null, analyzedDate: string | null): PlmObservationInput {
  return {
    materialIds: null,
    positiveStop: false, fibrous: false, nonFibrous: false,
    homogeneous: false, nonHomogeneous: false, layered: false, otherHomogenity: false,
    homogenityText: null,
    colorBeige: false, colorBlack: false, colorBlue: false, colorBrown: false,
    colorClear: false, colorGray: false, colorGreen: false, colorOrange: false,
    colorPink: false, colorRed: false, colorSilver: false, colorTan: false,
    colorPurple: false, colorWhite: false, colorYellow: false, colorCyan: false,
    colorMagenta: false, colorTeal: false, colorGold: false, colorOther: false,
    variousColors: false, colorText: null,
    cementitious: false, granular: false, firm: false, soft: false,
    paperLike: false, otherTexture: false, textureText: null,
    treatmentDissolve: false, treatmentAsh: false, treatmentGrind: false,
    treatmentHeat: false, treatmentMatrix: false, treatmentNonFriable: false,
    treatmentSolventTreated: false, treatmentTease: false, treatmentOther: false,
    fibersPresent: false, friability: null, fiberObservation: null, noAsbestosDetected: false,
    microscopeId: null, microscope: null, temperature: null, dispersionOilOid: null,
    hotPlate: null, thermohydrometer: null, dailyQcId: null, station: null, contaminationCheck: false,
    sampleNoteOid: null, analyzedByOid, analyzedDate,
  };
}

/** "Never" · "today" · "3 d ago" — the picker's Last run cell. */
export function lastRunText(s: Pick<PlmDailyQcSlide, 'daysAgo'>): string {
  if (s.daysAgo == null) return 'Never';
  return s.daysAgo <= 0 ? 'today' : `${s.daysAgo} d ago`;
}

/** The Reference Slide window's search: every word must appear in the slide's
 *  Reference ID, Original Source ID, Source, Matrix, Material or Asbestos Type. */
export function filterSlides(slides: PlmDailyQcSlide[], search: string): PlmDailyQcSlide[] {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return slides;
  return slides.filter(s => {
    const hay = [s.referenceId, s.originalSourceId, s.source, s.matrix, s.material, s.asbestosType]
      .filter(Boolean).join(' ').toLowerCase();
    return words.every(w => hay.includes(w));
  });
}

/** "today" · "1 day ago" · "3 days ago" — for sentences. */
export function daysAgoText(daysAgo: number | null): string {
  if (daysAgo == null) return 'never';
  if (daysAgo <= 0) return 'today';
  return `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
}

const num = (v: number | null | undefined) => (v == null ? '—' : String(Number(v.toFixed(2))));

/** "Chrysotile 8%" / "ND" — what the analyst read. */
export function readSummary(r: { readTypes: string | null; readValue: number | null }): string {
  if (!r.readTypes) return '—';
  return r.readTypes === 'ND' ? 'ND' : `${r.readTypes} ${num(r.readValue)}%`;
}

/** "Chrysotile 5% (1–15%)" — the reference, once revealed.
 *
 *  What Submit reveals is the VALUE and the range. A read on a slide that holds
 *  several asbestos types carries its type from the start (the analyst picked it),
 *  so the type alone must not count as "revealed" — it rendered "Actinolite —% (—–—%)". */
export function referenceSummary(r: {
  refType: string | null; refValue: number | null; refLow: number | null; refHigh: number | null;
}): string {
  if (!r.refType || r.refValue == null) return '🔒 hidden until submitted';
  return `${r.refType} ${num(r.refValue)}% (${num(r.refLow)}–${num(r.refHigh)}%)`;
}

/** What a refused request says: the API's own message when it sent one (its
 *  envelope's `error.message`), else the fallback. The Daily QC actions used to
 *  drop a refusal, so the button simply appeared to do nothing. */
export function refusalText(e: unknown, fallback: string): string {
  const message = (e as { response?: { data?: { error?: { message?: unknown } } } } | null)
    ?.response?.data?.error?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

// ── G5 PLM Daily QC behaviour (PlmV2DailyQcController, ported 2026-10-16) ────

/** yyyy-MM-dd of a server date, without a UTC shift. */
const dayOf = (iso: string | null | undefined) => (iso ?? '').slice(0, 10);

/** What still allows the analyst's PLM analysis today besides this read (G5
 *  `AllowedBy`): another read that passed today, or a supervisor override.
 *  Null when nothing does — a failed submit then blocks the day. */
export function allowedBy(
  status: Pick<PlmDailyQcStatus, 'state' | 'today' | 'date'> | null,
  rec: Pick<PlmDailyQcRow, 'oid' | 'qcDate'>,
): string | null {
  if (!status || !status.date || dayOf(status.date) !== dayOf(rec.qcDate)) return null;
  if (status.state === 'override') return 'a supervisor override is recorded';
  if (status.state === 'pass' && status.today && status.today.oid !== rec.oid) {
    return `${status.today.dailyQcId ?? 'another Daily QC'} passed earlier`;
  }
  return null;
}

/** The Submit confirmation (G5): "Submit DQC-…? Result: FAIL. PLM analysis
 *  stays blocked today." — or "Submit this Daily QC?" before the read is graded. */
export function submitConfirmText(
  dailyQcId: string | null, result: 'Pass' | 'Fail' | null | undefined, allowed: string | null,
): string {
  if (!result) return 'Submit this Daily QC?';
  return `Submit ${dailyQcId ?? 'this Daily QC'}? Result: ${result.toUpperCase()}.`
    + (result !== 'Pass' && !allowed ? ' PLM analysis stays blocked today.' : '');
}

/** What a submit says (G5 Submit_Execute). */
export function submittedText(result: 'Pass' | 'Fail' | null, blocked: boolean): { tone: 'success' | 'warning'; text: string } {
  if (result === 'Pass') return { tone: 'success', text: 'Submitted. Daily QC passed.' };
  return blocked
    ? { tone: 'warning', text: 'Submitted. Daily QC failed — PLM analysis is blocked today.' }
    : { tone: 'warning', text: 'Submitted. Daily QC failed.' };
}

/** The records in Previous / Next order (G5 `Order`: newest Daily QC ID first). */
export function dailyOrder<T extends Pick<PlmDailyQcRow, 'dailyQcId'>>(rows: readonly T[]): T[] {
  return [...rows].sort((a, b) => (b.dailyQcId ?? '').localeCompare(a.dailyQcId ?? ''));
}

/** A number as G5 shows it ("0.###"). */
export const num3 = (v: number | null | undefined) => (v == null ? '—' : String(Number(v.toFixed(3))));

/** Pass / Fail / Pending — a record's result, as its pill reads. */
export const dailyResult = (r: Pick<PlmDailyQcRow, 'submittedAt' | 'result'>): 'Pass' | 'Fail' | 'Pending' =>
  !r.submittedAt ? 'Pending' : r.result === 'Pass' ? 'Pass' : 'Fail';

/** The Daily QC bar above the sequence (prototype `renderDaily`): its tone and text. */
export function dailyBanner(s: PlmDailyQcStatus): { tone: 'warn' | 'ok' | 'fail' | 'off'; text: string } {
  const t = s.today;
  const id = t?.dailyQcId ?? '';
  const read = t ? readSummary(t) : '';
  const ref = t ? referenceSummary(t) : '';
  const map: Record<PlmDailyQcState, { tone: 'warn' | 'ok' | 'fail' | 'off'; text: string }> = {
    off: { tone: 'off', text: 'Daily QC is not enforced — the Reference Library has no reference slide to read yet.' },
    none: { tone: 'warn', text: '⚠ No Daily QC today — your sample layers are locked.' },
    pending: { tone: 'warn', text: `⏳ Daily QC ${id} not submitted — your sample layers are locked.` },
    pass: { tone: 'ok', text: `✓ Passed — ${id} · ${read} vs ${ref}` },
    fail: { tone: 'fail', text: `✗ Failed — ${id}. Analysis is blocked.` },
    override: { tone: 'ok', text: '✓ Supervisor override recorded — analysis allowed today (audited).' },
  };
  return map[s.state];
}

// ── The Daily QC switch (AI_AppSettings 'PlmDailyQcGate', 2026-10-17) ──────────
// 'Enforce' = the strict behaviour above; anything else = OPTIONAL "for now": no
// layer is locked, the Daily QC ID is not required, and the picker also offers the
// analyst's pending reads of today. The server applies the same switch.

/** The bar's note while the Daily QC is optional and today's read has not passed. */
/** G5 PlmV2Workspace's optional note, word for word (owner 2026-10-19). */
export const DAILY_QC_OPTIONAL_TEXT = 'Daily QC not passed today — optional for now; your layers stay editable.';

/** May the analyst enter results today? Always while the Daily QC is optional;
 *  otherwise as the day's state says (no status = not gated). */
export const dailyAllows = (s: PlmDailyQcStatus | null, enforced: boolean): boolean =>
  !enforced || !s || s.allows;

/** Is the Daily QC ID required on Save? Only while enforced and the gate is on. */
export const dailyQcIdRequired = (s: PlmDailyQcStatus | null, enforced: boolean): boolean =>
  enforced && !!s?.gateActive;

/** The Daily QC bar: the blocking states turn into an amber note while it is optional. */
export function dailyBar(s: PlmDailyQcStatus, enforced: boolean): { tone: 'warn' | 'ok' | 'fail' | 'off'; text: string } {
  if (enforced || s.allows) return dailyBanner(s);
  // G5 names the read: "(DQC… not submitted)" / "(DQC… failed)".
  const id = s.state === 'pending' ? s.pendingDailyQcId ?? s.today?.dailyQcId : s.today?.dailyQcId;
  const state = s.state === 'pending' && id ? ` (${id} not submitted)` : s.state === 'fail' && id ? ` (${id} failed)` : '';
  return { tone: 'warn', text: state ? DAILY_QC_OPTIONAL_TEXT.replace(' — ', `${state} — `) : DAILY_QC_OPTIONAL_TEXT };
}

/** The Daily QC ID as the picker lists it — a pending read says so. */
export const dailyChoiceLabel = (c: PlmDailyQcChoice): string =>
  `${c.dailyQcId ?? ''}${c.pending ? ' (pending)' : ''}`;

/** The read auto-filled into a layer with no Daily QC ID: the analyst's newest
 *  PASSED read of that day — never a pending one. */
export function autoDailyPick(choices: readonly PlmDailyQcChoice[], analystOid: string, day: string): PlmDailyQcChoice | undefined {
  const a = analystOid.toLowerCase();
  return choices
    .filter(c => !c.pending && c.dailyQcId && c.analystOid.toLowerCase() === a && c.qcDate.slice(0, 10) === day)
    .sort((x, y) => y.qcDate.localeCompare(x.qcDate))[0];
}

/** Daily QC records as QC Results list rows (prototype v13 `qcRecords`, the
 *  Reference branch): QC ID = the DQC id, batch "Daily QC", sample = the slide. */
export function dailyQcRecords(rows: readonly PlmDailyQcRow[]): QcRecord[] {
  return rows.map(r => ({
    key: `dq:${r.oid}`,
    qcId: r.dailyQcId ?? '—',
    batchOid: null,
    batchId: 'Daily QC',
    type: 'Reference',
    sample: `${r.referenceId ?? ''}${r.originalSourceId ? ` (${r.originalSourceId})` : ''}`,
    analysts: r.analystName ?? '—',
    date: r.qcDate,
    result: dailyResult(r),
    notBlind: r.reopened,
  }));
}

/** The Result filter of the PLM Daily QC list (G5 PlmV2DailyListResult). */
export type DailyResultFilter = 'All' | 'Pass' | 'Fail' | 'Pending';
export const DAILY_RESULT_FILTERS: DailyResultFilter[] = ['All', 'Pass', 'Fail', 'Pending'];

/** The Analyst filter's choices: every analyst with a read, by name (G5 PlmV2DailyListAnalyst). */
export function dailyAnalystOptions(rows: readonly PlmDailyQcRow[]): { value: string; label: string }[] {
  const seen = new Map<string, string>();
  for (const r of rows) {
    const key = r.analystOid.toLowerCase();
    if (!seen.has(key)) seen.set(key, r.analystName ?? '?');
  }
  return [...seen].map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Pending until submitted, then Submitted — G5's Status column (EnrichDaily). */
export const dailyStatusText = (r: Pick<PlmDailyQcRow, 'submittedAt'>): 'Pending' | 'Submitted' =>
  (r.submittedAt ? 'Submitted' : 'Pending');

/** G5's toolbar Search: every word must appear somewhere in the row. */
export function searchDailyRows(rows: PlmDailyQcRow[], search: string): PlmDailyQcRow[] {
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return rows;
  return rows.filter(r => {
    const hay = [r.dailyQcId, r.analystName, r.referenceId, r.originalSourceId, r.matrix, r.source, r.plmScope,
      dailyResult(r), dailyStatusText(r)].filter(Boolean).join(' ').toLowerCase();
    return words.every(w => hay.includes(w));
  });
}

/** The strip under the PLM Daily QC title — G5 PlmV2DailyUi.GateHtml, word for word. */
export function dailyGateStrip(s: PlmDailyQcStatus): { tone: 'ok' | 'warn' | 'bad' | 'mute'; day: string; text: string } {
  const id = s.today?.dailyQcId ?? '';
  const day = fmtLocalDate(nowClientWire());
  switch (s.state) {
    case 'pass': return { tone: 'ok', day, text: `✓ Passed — ${id}` };
    case 'override': return { tone: 'ok', day, text: '✓ Supervisor override — analysis is allowed today.' };
    case 'fail': return { tone: 'bad', day, text: `✗ Failed — ${id}. PLM analysis stays blocked today.` };
    case 'pending': return { tone: 'warn', day, text: `⏳ ${id} not submitted yet.` };
    case 'off': return { tone: 'mute', day, text: 'No reference slide is set up — the Daily QC is not enforced.' };
    default: return { tone: 'warn', day, text: '⚠ Not run today — PLM analysis is blocked until it passes.' };
  }
}

/** The reads the two filters let through; an empty analyst means every analyst. */
export function filterDailyRows(
  rows: readonly PlmDailyQcRow[], analystOid: string, result: DailyResultFilter,
): PlmDailyQcRow[] {
  const analyst = analystOid.toLowerCase();
  return rows.filter(r => (!analyst || r.analystOid.toLowerCase() === analyst)
    && (result === 'All' || dailyResult(r) === result));
}

// ── Browsing and Delete (G5 PlmV2DailyQcController / PlmV2DailyListController, 2026-10-17) ──

/** The view-mode note of the read on screen (G5 RefreshPanels, word for word);
 *  null while the analyst is entering their own pending read. */
export function dailyViewReason(
  rec: Pick<PlmDailyQcRow, 'analystName' | 'submittedAt'>, mine: boolean, entering: boolean,
): string | null {
  if (!mine) return `View mode — Daily QC by ${rec.analystName ?? 'another analyst'}.`;
  if (rec.submittedAt) return 'View mode — submitted (changes only through Rollback, with a reason).';
  return entering ? null : DAILY_VIEW_CONTINUE;
}

/** The view note of the analyst's own pending read reached while browsing: the form
 *  has no Edit — the list's Edit opens it for entry (owner 2026-10-19, G5 parity). */
export const DAILY_VIEW_CONTINUE = 'View mode — to continue your entry, go Back and use Edit on the Daily QC list.';

/** The Daily QC list's Edit refusal (G5 PlmV2DailyListEdit word for word; owner 2026-10-19, G5 parity). */
export const DAILY_LIST_EDIT_REFUSED = 'Only your own Daily QC that is not submitted yet can be edited — open it to view it.';

/** The Daily QC list's Rollback refusal (G5 PlmV2DailyListRollback word for word; owner 2026-10-19, G5 parity). */
export const DAILY_LIST_ROLLBACK_REFUSED = 'Only a submitted Daily QC can be rolled back.';

const isMine = (row: Pick<PlmDailyQcRow, 'analystOid'>, me: string | null | undefined) =>
  !!me && !!row.analystOid && row.analystOid.toLowerCase() === me.toLowerCase();

/** The list's Edit (owner 2026-10-19, G5 parity: PlmV2DailyListEdit): the ticked read
 *  opens for entry only when it is the signed-in analyst's own and not submitted;
 *  null = it may be opened for entry, else the refusal to show. */
export function dailyListEditRefusal(
  row: Pick<PlmDailyQcRow, 'analystOid' | 'submittedAt'>, me: string | null | undefined,
): string | null {
  return isMine(row, me) && !row.submittedAt ? null : DAILY_LIST_EDIT_REFUSED;
}

/** The list's Rollback, the same as QC Batch's (owner 2026-10-19, G5 PlmV2DailyListRollback):
 *  any SUBMITTED read, whoever analysed it; null = it may be rolled back, else the refusal. */
export function dailyListRollbackRefusal(
  row: Pick<PlmDailyQcRow, 'analystOid' | 'submittedAt'>, _me?: string | null,
): string | null {
  return row.submittedAt ? null : DAILY_LIST_ROLLBACK_REFUSED;
}

/** Whether the list's Rollback is enabled for the ticked reads, and its tooltip — QC Batch
 *  parity: one or several ticked reads, every one submitted. */
export function dailyListRollbackGate(
  rows: readonly Pick<PlmDailyQcRow, 'analystOid' | 'submittedAt'>[],
): { enabled: boolean; title: string } {
  if (rows.length === 0) return { enabled: false, title: 'Tick the submitted Daily QC to roll back' };
  if (rows.some(r => !r.submittedAt)) return { enabled: false, title: DAILY_LIST_ROLLBACK_REFUSED };
  return { enabled: true, title: 'Re-open the ticked submitted Daily QC (reason required, audited)' };
}

/** Whether the list's Edit / Rollback is enabled for the ticked rows, and its tooltip
 *  (owner 2026-10-19, G5 parity: toolbar-only actions whose criteria are "exactly one
 *  ticked read — the analyst's own, not submitted / submitted"). `row` is the one
 *  ticked read when exactly one is ticked. */
export function dailyListActionGate(
  action: 'edit' | 'rollback', tickedCount: number,
  row: Pick<PlmDailyQcRow, 'analystOid' | 'submittedAt'> | null, me: string | null | undefined,
): { enabled: boolean; title: string } {
  const edit = action === 'edit';
  if (tickedCount > 1) return { enabled: false, title: edit ? 'Tick one Daily QC to edit.' : 'Tick one Daily QC to roll back.' };
  if (tickedCount === 0 || !row) {
    return { enabled: false, title: edit
      ? 'Tick one Daily QC to edit — your own read that is not submitted yet'
      : 'Tick one Daily QC to roll back — your own submitted read' };
  }
  const refusal = edit ? dailyListEditRefusal(row, me) : dailyListRollbackRefusal(row, me);
  if (refusal) return { enabled: false, title: refusal };
  return { enabled: true, title: edit
    ? 'Continue the ticked Daily QC — your own read that is not submitted yet'
    : 'Re-open the ticked submitted Daily QC — your own read (reason required, audited)' };
}

/** The list's Rollback prompt (G5 PlmV2DailyListRollback word for word). */
export const dailyListRollbackPrompt = (dailyQcId: string | null | undefined): string =>
  `Re-open the submitted Daily QC ${dailyQcId ?? ''}? Reason (required — audited):`;

/** The disabled Delete's tooltip on the Daily QC screen. */
export const DAILY_DELETE_LOCKED_TIP = 'Only a pending Daily QC that no PLM layer uses can be deleted.';
/** The refusals of DELETE /daily-qc/{oid}, in the API's words (PlmDailyQcController). */
export const DAILY_DELETE_LOCKED_TEXT = 'This Daily QC is submitted or used by a PLM layer — it cannot be deleted.';
export const DAILY_DELETE_NOT_YOURS_TEXT = 'Only the analyst who runs a Daily QC (or an administrator) can delete it.';
/** The delete's required reason prompt. */
export const DAILY_DELETE_REASON = {
  message: 'Enter a reason for deleting this Daily QC.', placeholder: 'Reason for change (required)',
} as const;

/** The delete confirm. */
export const dailyDeleteMessage = (dailyQcId: string | null | undefined): string =>
  `Delete Daily QC ${dailyQcId ?? ''}? The deletion is written to the audit trail.`;

/** Why a read cannot be deleted (the server's rule, from its answer for the read):
 *  submitted or used by a layer, else not the user's own; null = it can. */
export function dailyDeleteRefusal(
  d: { record: Pick<PlmDailyQcRow, 'submittedAt'>; used?: boolean; canDelete?: boolean },
): string | null {
  if (d.record.submittedAt || d.used) return DAILY_DELETE_LOCKED_TEXT;
  return d.canDelete ? null : DAILY_DELETE_NOT_YOURS_TEXT;
}

// ── owner 2026-10-19, G5 parity: New gate · Reference Slide ID pick list · multi-delete ──

/** New's tooltip while it may be used (G5 PlmV2DailyUi.GateNew). */
export const DAILY_NEW_TIP = "Start today's Daily QC: pick a reference slide";

/** New's tooltip while it is disabled: the analyst's latest read of today is not
 *  submitted (the server's `pendingDailyQcId`, G5 GateNew word for word). */
export const dailyNewBlockedTip = (pendingDailyQcId: string): string =>
  `${pendingDailyQcId} is not submitted yet — submit it (or delete it) before starting a new Daily QC.`;

/** The text a reference slide is shown and picked by: its Reference Slide ID only — the
 *  Original Source ID is a column of the pick list (owner 2026-10-19, G5 parity:
 *  PlmV2Entry.SlideLabel returns the ID; "This ID is enough here"). The second
 *  argument is kept for the call sites and ignored. */
export const slideLabel = (referenceId: string | null | undefined, _originalSourceId?: string | null): string =>
  referenceId ?? '';

/** The Reference Slide ID pick list (G5 FillReference): the slides the New picker
 *  offers — Reference Slide ID | Original Source ID | Matrix | Source | Last Run.
 *  The read's own slide stays listed (first) when it is no longer offered. */
export function dailySlideLookup(
  slides: readonly PlmDailyQcSlide[],
  current: { referenceId: string | null; originalSourceId: string | null; matrix: string | null; source: string | null } | null,
): LookupData {
  const rows = slides.map(s => [
    s.referenceId ?? '', s.originalSourceId ?? '', s.matrix ?? '', s.source ?? '',
    s.lastRun ? fmtLocalDate(s.lastRun) : 'Never',
  ]);
  if (current) {
    const label = slideLabel(current.referenceId, current.originalSourceId);
    if (label && !slides.some(s => slideLabel(s.referenceId, s.originalSourceId) === label)) {
      rows.unshift([current.referenceId ?? '', current.originalSourceId ?? '', current.matrix ?? '', current.source ?? '', '']);
    }
  }
  return { cols: ['Reference Slide ID', 'Original Source ID', 'Matrix', 'Source', 'Last Run'], rows };
}

/** The one reason prompt of the list's Delete (G5 Delete_Execute): the reads that
 *  will be deleted, and those ticked that cannot be and are left. */
export function dailyMultiDeleteMessage(ids: readonly string[], refused: readonly string[]): string {
  const skip = refused.length ? ` (${refused.join(', ')} cannot be deleted and is left.)` : '';
  return `Delete ${ids.join(', ')}? ${ids.length === 1 ? 'It is' : 'They are'} removed from the Daily QC list.${skip}`
    + ' Reason (required — audited):';
}

/** Several reads ticked and none of them may be deleted (G5). */
export const DAILY_DELETE_NONE = 'None of the ticked Daily QC can be deleted — only a pending read that no PLM layer uses '
  + '(your own; an administrator may delete any).';
/** After the list's Delete (G5): the reads deleted, and those left. */
export const dailyDeletedText = (ids: readonly string[]): string => `${ids.join(', ')} deleted successfully.`;
export const dailyNotDeletedText = (ids: readonly string[]): string =>
  `Not deleted: ${ids.join(', ')} — submitted or used by a PLM layer.`;
